// Осмотры кабинета на календаре пролётов: какие осмотры относятся к снимку,
// сколько на снимке отметок, куда вести старую ссылку `pic=<номер осмотра>`.
//
// Осмотр и пролёт названы одним ключом — `источник:номер сцены`: оба берут
// сцену из одних каталогов STAC. Прибор у осмотра зовётся `sar`, у каталога
// пролётов — `radar`; ключу это не мешает.

import { sceneKey } from './datasetCalendar'
import type { DatasetMark } from './datasetLayer'
import type { MarkProps } from './MarkCard'

interface LookedFeature { properties?: Record<string, unknown> | null }
interface MarkFeature { properties: Record<string, unknown> }

function lookedKey(f: LookedFeature): string | null {
  const p = f.properties
  if (!p || typeof p.source !== 'string' || typeof p.scene_id !== 'string') return null
  return sceneKey({ source: p.source, scene_id: p.scene_id })
}

/** Номера осмотров снимка. */
export function sweepsOfScene(looked: LookedFeature[], scene: string): Set<number> {
  return new Set(looked.filter(f => lookedKey(f) === scene)
    .map(f => Number(f.properties?.sweep_id)))
}

/** Отметки осмотров по снимкам: ключ снимка → число отметок. */
export function markCountsByScene(looked: LookedFeature[], marks: MarkFeature[]): Map<string, number> {
  const sceneOfSweep = new Map<number, string>()
  for (const f of looked) {
    const key = lookedKey(f)
    if (key) sceneOfSweep.set(Number(f.properties?.sweep_id), key)
  }
  const out = new Map<string, number>()
  for (const m of marks) {
    const key = sceneOfSweep.get(Number(m.properties.sweep_id))
    if (key) out.set(key, (out.get(key) ?? 0) + 1)
  }
  return out
}

/** Старая ссылка «Осмотра»: номер осмотра → день и снимок календаря. */
export function sceneOfSweep(looked: LookedFeature[], sweepId: number):
    { day: string; scene: string } | null {
  const f = looked.find(x => Number(x.properties?.sweep_id) === sweepId)
  const key = f ? lookedKey(f) : null
  const at = f?.properties?.captured_at
  if (!key || typeof at !== 'string' || at.length < 10) return null
  return { day: at.slice(0, 10), scene: key }
}

/** Отметка датасета без пары АИС → общая карточка отметки. Прибор каталога
 *  (`radar`) переводится в имя осмотра (`sar`). */
export function datasetMarkProps(
  m: DatasetMark,
  scene: { sensor: string; captured_at: string } | null,
  thresholds: MarkProps['thresholds'],
): MarkProps {
  const ev: Record<string, number | boolean | null> = {}
  for (const [k, v] of Object.entries(m.evidence ?? {})) {
    if (typeof v === 'number' || typeof v === 'boolean' || v === null) ev[k] = v
  }
  return {
    sweep_id: 0, local_id: m.mark_id, verdict: m.verdict,
    length_m: m.length_m, width_m: null, area_m2: null, course_deg: null,
    lon: m.lon, lat: m.lat, evidence: ev,
    ais: {
      matched: false, source: null, matched_on: null,
      reason: m.in_tolerance ? 'ds_taken' : 'ds_unpaired',
      distance_m: m.nearest_record_m, course_delta_deg: null, random_pairs_expected: null,
      candidates: 0, mmsi: null,
    },
    sensor: scene?.sensor === 'radar' ? 'sar' : scene?.sensor,
    captured_at: scene?.captured_at,
    scene_id: m.scene_id, source: m.source,
    thresholds,
  }
}
